import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../API/authApi";
import { useProxySession } from "../contextProviders/ProxySessionProvider";

export const useAuthApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { startProxySession, endProxySession, isProxyMode, getAdminUser } = useProxySession();

  // Login function
  const login = useCallback(
    async ({ email, password }) => {
      var credentials = { email, password };
      setLoading(true);
      setError(null);
      try {
        const result = await authApi.login(credentials);
        if (result.error) {
          setError(result.error);
          return null;
        }

        const loginData = result.data;

        if (loginData && loginData.requiresTwoFactor) {
          // Store session info for 2FA verification
          localStorage.setItem(
            "loginSession",
            JSON.stringify({
              userId: loginData.userId,
              email: loginData.email,
              name: loginData.name,
              role: loginData.role,
              twoFactorMethod: loginData.twoFactorMethod,
              tempToken: loginData.tempToken,
            })
          );

          // Redirect to 2FA verification page
          navigate("/login-2fa");
          return { requiresTwoFactor: true };
        } else {
          // No 2FA required, complete login
          localStorage.setItem("token", loginData.token);
          localStorage.setItem("role", loginData.user.role);

          // Store complete user data for role-based access
          const userData = {
            id: loginData.user.id,
            email: loginData.user.email,
            name: loginData.user.name,
            role: loginData.user.role,
            isActive: loginData.user.isActive,
          };
          localStorage.setItem("user", JSON.stringify(userData));

          // Redirect to base dashboard for each role
          const roleBasePath = {
            SuperAdmin: "/superadmin",
            ClientAdmin: "/clientadmin",
            Operator: "/operator",
            Consumer: "/consumer",
          };
          navigate(roleBasePath[loginData.user.role] || "/unauthorized");
          return { success: true, user: userData };
        }
      } catch (err) {
        setError("Login failed. Please try again.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  // Send Reset Password Request
  const sendResetPasswordRequest = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authApi.sendResetPasswordRequest(id);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to send reset password request. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password function
  const resetPassword = useCallback(async ({ token, password }) => {
    var resetData = { token, password };
    setLoading(true);
    setError(null);
    try {
      const result = await authApi.resetPassword(resetData);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Password reset failed. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Request password reset function
  const requestForgotPassword = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authApi.requestForgotPassword(email);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to send password reset email. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  //Resend Verification Email
  const resendVerificationEmail = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authApi.resendVerificationEmail(id);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to resend verification email. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Consumer Reset Password function
  const consumerResetPassword = useCallback(async ({ token, newPassword }) => {
    setLoading(true);
    setError(null);
    var resetData = { token, newPassword };
    try {
      const result = await authApi.consumerResetPassword(resetData);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to reset password. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Stop SignalR connection BEFORE clearing token so the server-side
      // OnDisconnectedAsync fires and updates the online operator count
      window.dispatchEvent(new Event('app-logout'));
      // Small delay to allow SignalR stop() to complete
      await new Promise((resolve) => setTimeout(resolve, 300));
      await authApi.logout();
    } catch (err) {
      console.error("Logout API call failed:", err);
    } finally {
      // Always clear proxy session state
      endProxySession();
      // Remove proxySession explicitly for extra safety
      if (localStorage.getItem("proxySession")) {
        localStorage.removeItem("proxySession");
      }
      // Clear local storage regardless of API call result
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("user");
      localStorage.removeItem("loginSession");
  // Redirect to base URL
  navigate("/");
      setLoading(false);
    }
  }, [navigate, endProxySession]);

  // Proxy login function - Super Admin impersonates another user
  const proxyLogin = useCallback(
    async ({ targetUserId, reason = "Administrative assistance" }) => {
      setLoading(true);
      setError(null);
      try {
        const result = await authApi.proxyLogin({ targetUserId, reason });
        if (result.error) {
          setError(result.error);
          return null;
        }

        const proxyData = result.data;
        
        // Start proxy session with the received data
        startProxySession(proxyData);

        // Navigate to the proxy user's dashboard
        const roleBasePath = {
          SuperAdmin: "/superadmin",
          ClientAdmin: "/clientadmin", 
          Operator: "/operator",
          Consumer: "/consumer",
        };
        
        navigate(roleBasePath[proxyData.proxyUser.role] || "/unauthorized");
        
        return { 
          success: true, 
          proxyUser: proxyData.proxyUser,
          adminUser: proxyData.adminUser,
          message: proxyData.message
        };
      } catch (err) {
        setError("Proxy login failed. Please try again.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, startProxySession]
  );

  // Exit proxy session function
  const exitProxy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const proxySessionData = localStorage.getItem('proxySession');
      if (!proxySessionData) {
        setError("No active proxy session found");
        return null;
      }

      const parsedSession = JSON.parse(proxySessionData);
      
      // Call API to end proxy session and get fresh admin token
      const result = await authApi.exitProxy({ 
        proxySessionId: parsedSession.proxySessionId 
      });
      
      if (result.error) {
        setError(result.error);
        return null;
      }

      const exitData = result.data;
      
      // Update token and user data with fresh admin token and info
      localStorage.setItem('token', exitData.adminToken);
      localStorage.setItem('role', exitData.adminUser.role);
      localStorage.setItem('user', JSON.stringify(exitData.adminUser));
      
      // End local proxy session
      endProxySession();

      // Navigate back to admin dashboard
      const roleBasePath = {
        SuperAdmin: "/superadmin",
        ClientAdmin: "/clientadmin",
        Operator: "/operator",
        Consumer: "/consumer",
      };
      
      navigate(roleBasePath[exitData.adminUser.role] || "/unauthorized");
      
      return { 
        success: true, 
        message: exitData.message || "Proxy session ended successfully",
        adminUser: exitData.adminUser
      };
    } catch (err) {
      setError("Failed to exit proxy session. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [navigate, endProxySession]);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    login,
    sendResetPasswordRequest,
    resetPassword,
    requestForgotPassword,
    resendVerificationEmail,
    logout,
    proxyLogin,
    exitProxy,
    clearError,
    consumerResetPassword,
  };
};
